<script setup lang="ts">
/**
 * 电子宠物(MVP)—— 一只 🦀 沿窗口底边横着遛,点它在头顶弹气泡接 iris 聊天。
 *
 * · 形象:emoji 占位(满意后再升级 SVG / 精灵图,见 usePet.PetKind)。
 * · 走动:position:absolute + `left` 过渡,随机挑目标点、到点歇一会儿再走;螃蟹横行,
 *   正好不用转身(只做个朝向翻转 + 走路轻微上下颠)。
 * · 聊天:走 api/pet 的独立流式接口(/pet/chat),独立 session(fabric.pet_session)+ iris
 *   另一个 host(fabric-pet)→ 跟智能客服各自人设、记忆互不串。点开时冻结螃蟹位置免气泡飘。
 * · 全局挂 AppLayout;不带遮罩,只有螃蟹和气泡接收点击,其余照常操作。代登录隐藏。
 */
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { usePet } from '@/composables/usePet'
import { useAuthStore } from '@/stores/auth'
import { fetchPetState, resetPetSession, streamPetChat, petSessionId } from '@/api/pet'
import {
  buildReminderBody,
  createReminder,
  deleteReminder,
  fetchReminderInbox,
  fetchReminders,
  formatReminderSchedule,
  parseReminderIntent,
  patchReminder,
  type PetReminder
} from '@/api/petReminders'
import {
  describeParsedReminder,
  looksLikeReminderIntent,
  tryParseReminderIntent
} from '@/utils/parseReminderIntent'
import { isTauri } from '@/composables/useDesktopBridge'
import { isLocalDesktop } from '@/edition'

interface ChatMsg {
  role: 'user' | 'assistant'
  content: string
}

const { petEnabled } = usePet()
const auth = useAuthStore()
const hasPetCap = computed(() => auth.hasCapability('pet_companion'))

// 部署形态决定 LLM key 配置入口:仅单机版在「系统设置 → 套餐与 AI」自填 key;
// 云版 / 浏览器由平台运营后台统一配置。
const needsLocalAiKey = isTauri() && isLocalDesktop()
// 闲聊模型不可用时的引导文案 —— 按部署形态分叉(见 SupportDrawer 同款处理)。
const CHAT_UNAVAILABLE_MSG = needsLocalAiKey
  ? '小蟹还没接通大脑呢~ 暂时聊不了天。让管理员去「系统设置 → 套餐与 AI」把大模型接上,我就能陪你唠了 🦀'
  : '小蟹这会儿还没通上电,没有能量,聊不了天~'

// 螃蟹宽高 / 落地高度 / 速度 —— 调这几个数就能改观感。
const SIZE = 44
// 离窗口底边的高度。抬高到能避开 Windows 任务栏(~48px):蟹是 fixed 在窗口底部的,
// 缩窗/还原时窗口内容底边会和任务栏重叠,贴最底(12px)就会被任务栏遮掉看不见。
const FLOOR = 56
// 走速随心情(px/秒):好=轻快 / 一般 / 不好=蔫慢。见 docs/plans/pet-mood-growth.md §5.2。
const SPEED_BY_MOOD = { good: 70, normal: 58, bad: 42 } as const
const SIDEBAR = 168 // 别走进左侧 sidebar
const BUBBLE_W = 300

// ── 心情 + 成长态(后端 GET /pet/state;全程隐式,只拿档不拿数值)──
type MoodTier = 'good' | 'normal' | 'bad'
const moodTier = ref<MoodTier>('normal')
const petScale = ref(1.2) // 蜕壳成长尺寸倍率 1.2–1.6(出生即 1.2×)
const molting = ref(false) // 蜕壳「时刻」动画态
const moltToast = ref<string | null>(null) // 蜕壳文案气泡
const curSpeed = () => SPEED_BY_MOOD[moodTier.value]

let moltAnimTimer: ReturnType<typeof setTimeout> | undefined
let moltToastTimer: ReturnType<typeof setTimeout> | undefined

// ── 定时提醒(P0:轮询 inbox + 静态文案气泡,不走 LLM)────────────────
const remindersOpen = ref(false)
const reminders = ref<PetReminder[]>([])
const remindersLoading = ref(false)
const reminderSaving = ref(false)
const remTitle = ref('')
const remSchedule = ref<'once' | 'daily' | 'weekly' | 'every'>('daily')
const remOnceLocal = ref('')
const remTime = ref('10:00')
const remWeekday = ref(1)
const remEveryHours = ref(2)
const reminderToast = ref<string | null>(null)
const reminderQueue: ReminderInboxEvent[] = []
let reminderToastTimer: ReturnType<typeof setTimeout> | undefined
let inboxPollTimer: ReturnType<typeof setInterval> | undefined

interface ReminderInboxEvent {
  id: number
  reminder_id: number
  title: string
  fire_at: string
}

// 已向用户展示过的蜕壳次数。首次见到某 molt_count 才庆祝一次(纯日历到点长大)。
const MOLT_KEY = 'fabric.pet.molt'
function lastShownMolt(): number | null {
  const raw = localStorage.getItem(MOLT_KEY)
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function celebrateMolt() {
  molting.value = true
  moltToast.value = '我蜕壳啦,又长大了一点 🦀'
  clearTimeout(moltAnimTimer)
  clearTimeout(moltToastTimer)
  moltAnimTimer = setTimeout(() => (molting.value = false), 1000)
  moltToastTimer = setTimeout(() => (moltToast.value = null), 2800)
}

async function loadPetState() {
  if (!petEnabled.value || !hasPetCap.value || auth.isImpersonating) return
  try {
    const s = await fetchPetState()
    moodTier.value = s.mood_tier
    petScale.value = s.scale
    // 蜕壳庆祝:只在「比上次展示的次数多」时播一次。首见(无记录)直接静默记下,
    // 不为历史已发生的蜕壳补庆祝(避免换机/清缓存后炸一堆动画)。
    const shown = lastShownMolt()
    if (shown != null && s.molt_count > shown) celebrateMolt()
    try {
      localStorage.setItem(MOLT_KEY, String(s.molt_count))
    } catch {
      /* 隐私模式 → 忽略,下次再比 */
    }
  } catch {
    /* 取不到就维持现状(默认 normal / 1.0),不打扰用户 */
  }
}

function showNextReminder() {
  if (reminderToast.value) return
  const next = reminderQueue.shift()
  if (!next) return
  reminderToast.value = `⏰ 到点啦：${next.title}`
  clearTimeout(reminderToastTimer)
  act.value = 'cheer'
  clearTimeout(actTimer)
  actTimer = setTimeout(() => (act.value = 'idle'), 940)
  reminderToastTimer = setTimeout(() => {
    reminderToast.value = null
    showNextReminder()
  }, 4500)
}

async function pollInbox() {
  if (!petEnabled.value || !hasPetCap.value || auth.isImpersonating) return
  try {
    const { items } = await fetchReminderInbox()
    if (items.length) {
      reminderQueue.push(...items)
      showNextReminder()
    }
  } catch {
    /* 网络抖动 → 下轮再拉 */
  }
}

function startInboxPoll() {
  stopInboxPoll()
  void pollInbox()
  inboxPollTimer = setInterval(pollInbox, 60_000)
  window.addEventListener('focus', pollInbox)
}

function stopInboxPoll() {
  if (inboxPollTimer) clearInterval(inboxPollTimer)
  inboxPollTimer = undefined
  window.removeEventListener('focus', pollInbox)
}

async function loadReminders() {
  remindersLoading.value = true
  try {
    const res = await fetchReminders()
    reminders.value = res.items
  } catch {
    reminders.value = []
  } finally {
    remindersLoading.value = false
  }
}

function toggleReminders() {
  remindersOpen.value = !remindersOpen.value
  if (remindersOpen.value) void loadReminders()
}

async function saveReminder() {
  if (reminderSaving.value || !remTitle.value.trim()) return
  reminderSaving.value = true
  try {
    const body = buildReminderBody({
      title: remTitle.value,
      schedule: remSchedule.value,
      onceLocal: remOnceLocal.value,
      time: remTime.value,
      weekday: remWeekday.value,
      everyHours: remEveryHours.value
    })
    await createReminder(body)
    remTitle.value = ''
    await loadReminders()
  } catch (e) {
    const err = e as Error
    reminderToast.value = err.message || '保存失败'
    clearTimeout(reminderToastTimer)
    reminderToastTimer = setTimeout(() => (reminderToast.value = null), 2800)
  } finally {
    reminderSaving.value = false
  }
}

async function toggleReminderEnabled(r: PetReminder) {
  try {
    await patchReminder(r.id, { enabled: !r.enabled })
  } catch (e) {
    // 别静默:例如启用一条已过期的一次性提醒会被后端拒("时间须在未来"),要让用户看到原因。
    const err = e as Error
    reminderToast.value = err.message || '操作失败'
    clearTimeout(reminderToastTimer)
    reminderToastTimer = setTimeout(() => (reminderToast.value = null), 2800)
  } finally {
    await loadReminders()
  }
}

/** 一次性提醒已触发完成(enabled=false 且无下次执行)——不可再启用,UI 只留删除。 */
function reminderDone(r: PetReminder): boolean {
  return r.kind === 'at' && !r.enabled && !r.next_run_at
}

async function removeReminder(id: number) {
  try {
    await deleteReminder(id)
    reminders.value = reminders.value.filter((r) => r.id !== id)
  } catch {
    /* ignore */
  }
}

const crabEl = ref<HTMLElement | null>(null)
const scrollEl = ref<HTMLElement | null>(null)
const winW = ref(window.innerWidth)
const winH = ref(window.innerHeight)

const x = ref(260) // 螃蟹左边距(相对 pet-root,即视口)
const dur = ref(0) // 当前一段移动时长(s);0 = 不过渡(冻结)
const facing = ref<1 | -1>(1)
const moving = ref(false)
const chatOpen = ref(false)
const act = ref<'idle' | 'wave' | 'cheer' | 'hop' | 'pinch'>('idle') // 小动作:挥钳 / 双钳齐挥 / 蹦跳 / 夹钳
const floorY = ref(FLOOR) // 离窗口底的高度(默认 FLOOR);可按住拖动改变
const dragging = ref(false)

let walkTimer: ReturnType<typeof setTimeout> | undefined
let stopTimer: ReturnType<typeof setTimeout> | undefined
let actTimer: ReturnType<typeof setTimeout> | undefined

function clearAct() {
  clearTimeout(actTimer)
  act.value = 'idle'
}

// 站定时偶尔挥个钳子或蹦一下 —— 让它不只是「左右走」。聊天/移动/关灯时不触发。
function idleAct() {
  if (!petEnabled.value || chatOpen.value || moving.value) return
  const r = Math.random()
  // 挥右钳 25% / 双钳齐挥 23% / 夹钳 26% / 蹦跳 26%(挥舞类取「原来与上版」的中间)
  act.value = r < 0.25 ? 'wave' : r < 0.48 ? 'cheer' : r < 0.74 ? 'pinch' : 'hop'
  clearTimeout(actTimer)
  actTimer = setTimeout(() => (act.value = 'idle'), act.value === 'hop' ? 640 : 940)
}

function petBounds() {
  const min = SIDEBAR + 16
  const max = Math.max(min, winW.value - SIZE - 16)
  return { min, max }
}

function scheduleWalk(delay: number) {
  clearTimeout(walkTimer)
  walkTimer = setTimeout(walkStep, delay)
}

function walkStep() {
  if (!petEnabled.value || chatOpen.value) return
  const { min, max } = petBounds()
  const target = Math.round(min + Math.random() * (max - min))
  const dist = Math.abs(target - x.value)
  if (dist < 12) {
    scheduleWalk(700 + Math.random() * 1300) // 太近就再歇一下
    return
  }
  facing.value = target > x.value ? 1 : -1
  dur.value = Math.max(0.5, dist / curSpeed()) // 走速随心情
  clearAct() // 走起来就不要还卡在挥钳/蹦跳里
  moving.value = true
  x.value = target
  const ms = dur.value * 1000
  clearTimeout(stopTimer)
  stopTimer = setTimeout(() => {
    moving.value = false
    // 站定后做个小动作的概率:心情不好时明显变蔫(0.3),否则照旧(0.58)。
    if (Math.random() < (moodTier.value === 'bad' ? 0.3 : 0.58)) {
      clearTimeout(actTimer)
      actTimer = setTimeout(idleAct, 280) // 站定后约六成停一下做个小动作(挥钳/夹钳/蹦跳)
    }
  }, ms)
  scheduleWalk(ms + 800 + Math.random() * 1800) // 到点 + 歇一会儿 → 下一段
}

// ─── 气泡聊天(复用 iris 流式接口)───────────────────────────────────
const messages = ref<ChatMsg[]>([])
const input = ref('')
const sending = ref(false)
let sessionId = petSessionId()
let controller: AbortController | null = null

function scrollBottom() {
  void nextTick(() => {
    const el = scrollEl.value
    if (el) el.scrollTop = el.scrollHeight
  })
}

function toggleChat() {
  if (chatOpen.value) {
    chatOpen.value = false
    scheduleWalk(500) // 收起 → 接着遛
    return
  }
  // 打开:把螃蟹冻结在当前实际位置(可能正走到一半),免得气泡对不上 + 螃蟹继续滑。
  clearTimeout(walkTimer)
  const el = crabEl.value
  if (el) {
    const r = el.getBoundingClientRect()
    dur.value = 0
    x.value = Math.round(r.left)
  }
  moving.value = false
  clearAct()
  chatOpen.value = true
  scrollBottom()
}

async function send() {
  const content = input.value.trim()
  if (!content || sending.value) return
  input.value = ''
  messages.value.push({ role: 'user', content })
  messages.value.push({ role: 'assistant', content: '' })
  const assistant = messages.value[messages.value.length - 1]
  sending.value = true
  scrollBottom()

  // 设提醒 —— ① 本地正则(免费、即时,不计次) ② 像提醒则 LLM parse(建成扣 1 次,同聊天额度) ③ 否则闲聊。
  const parsed = tryParseReminderIntent(content)
  if (parsed) {
    try {
      await createReminder(parsed)
      assistant.content = `好哒~ ${describeParsedReminder(parsed)}我会提醒你：${parsed.title} 🦀`
    } catch (e) {
      const err = e as Error & { code?: string }
      if (err?.code === 'capability_not_enabled') {
        assistant.content = '电子宠物还没开通呢~ 让老板升级一下,我就能帮你设提醒啦 🦀'
      } else {
        assistant.content = err?.message?.includes('须在未来')
          ? '哎呀这个时间已经溜过去啦~ 换个往后的时间吧 🦀'
          : `没设成功:${err?.message ?? '请稍后重试'}。要不点右上角 ⏰ 小闹钟自己填一条?🦀`
      }
    } finally {
      sending.value = false
      scrollBottom()
    }
    return
  }

  if (looksLikeReminderIntent(content)) {
    try {
      const res = await parseReminderIntent(content)
      if (res.created && res.reply) {
        assistant.content = res.reply
        sending.value = false
        scrollBottom()
        return
      }
      // not_reminder / 未建成功 → 落到下面闲聊
    } catch (e) {
      const err = e as Error & { code?: string }
      if (err?.code === 'capability_not_enabled') {
        assistant.content = '电子宠物还没开通呢~ 让老板升级一下,我就能帮你设提醒啦 🦀'
        sending.value = false
        scrollBottom()
        return
      }
      if (err?.code === 'pet_quota_exhausted') {
        // 次数用完 —— 后端已给委婉话术(体力用完),直接展示,不加"没成功"前缀。
        assistant.content = err.message
        sending.value = false
        scrollBottom()
        return
      }
      if (err?.code === 'service_unavailable' || err?.message?.includes('未配置')) {
        // LLM 没接时,设提醒仍可走右上角 ⏰ 小闹钟表单,引导过去(别把技术报错甩给用户)。
        assistant.content =
          '这句的时间我一下没听准~ 点右上角 ⏰ 小闹钟直接填,准没错 🦀'
        // 继续落到闲聊? 用户本意是设提醒,直接展示错误更清晰。
        sending.value = false
        scrollBottom()
        return
      }
      assistant.content = `没设成功:${err?.message ?? '请稍后重试'}。要不点右上角 ⏰ 小闹钟自己填一条?🦀`
      sending.value = false
      scrollBottom()
      return
    }
  }

  controller = new AbortController()
  try {
    await streamPetChat({
      message: content,
      sessionId,
      signal: controller.signal,
      onToken: (delta) => {
        assistant.content += delta
        scrollBottom()
      }
    })
    if (!assistant.content) assistant.content = '咦,我没接住这句~ 再说一遍试试?'
  } catch (e) {
    const err = e as Error & { code?: string }
    if (err?.name === 'AbortError') {
      if (!assistant.content) messages.value.pop()
    } else if (err?.code === 'pet_quota_exhausted') {
      // 每日免费额度用尽 —— 后端已返回友好引导文案,直接展示(不加"出错了"前缀)。
      assistant.content = err.message
    } else if (err?.code === 'capability_not_enabled') {
      assistant.content = '电子宠物还没开通呢~ 让老板升级一下,我就能出来陪你唠啦 🦀'
    } else if (err?.code === 'service_unavailable') {
      assistant.content = CHAT_UNAVAILABLE_MSG
    } else {
      assistant.content = '哎呀我卡壳了~ 缓一下再问我一次呗 🦀'
    }
  } finally {
    sending.value = false
    controller = null
    scrollBottom()
    void loadPetState() // 聊一句可能改了心情 → 刷新档位/走速/颜色
  }
}

function newChat() {
  controller?.abort()
  sessionId = resetPetSession()
  messages.value = []
  input.value = ''
}

// 气泡居中对准螃蟹,左右夹在视口内;尾巴指回螃蟹中心。
const bubbleLeft = computed(() => {
  const center = x.value + SIZE / 2
  const raw = center - BUBBLE_W / 2
  return Math.min(Math.max(8, raw), Math.max(8, winW.value - BUBBLE_W - 8))
})
const tailLeft = computed(() => {
  const center = x.value + SIZE / 2
  return Math.min(Math.max(18, center - bubbleLeft.value), BUBBLE_W - 18)
})

// ─── 按住拖动挪位置 ───────────────────────────────────────────────
// 落点存 localStorage(下次还在那);轻点=开/收聊天,移动超过阈值才算拖。
const POS_KEY = 'fabric.pet.pos'
const DRAG_THRESHOLD = 4
let drag: { sx: number; sy: number; ox: number; oy: number; id: number; moved: boolean } | null = null

function floorBounds() {
  const min = 8
  return { min, max: Math.max(min, winH.value - SIZE - 8) }
}

function savePos() {
  try {
    localStorage.setItem(
      POS_KEY,
      JSON.stringify({ x: Math.round(x.value), y: Math.round(floorY.value) })
    )
  } catch {
    /* 隐私模式/配额满 → 忽略,内存态仍在 */
  }
}
function loadPos(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(POS_KEY)
    if (raw) {
      const o = JSON.parse(raw) as { x?: unknown; y?: unknown }
      if (typeof o.x === 'number' && typeof o.y === 'number') return { x: o.x, y: o.y }
    }
  } catch {
    /* 坏数据 → 用默认 */
  }
  return null
}

function onPointerDown(e: PointerEvent) {
  if (e.button !== 0) return // 仅左键
  const el = e.currentTarget as HTMLElement
  // 冻在当前真实位置(可能正走到一半)+ 暂停自动遛,免得按下/拖动时跳一下。
  clearTimeout(walkTimer)
  clearTimeout(stopTimer)
  clearAct()
  moving.value = false
  dur.value = 0
  x.value = Math.round(el.getBoundingClientRect().left)
  drag = { sx: e.clientX, sy: e.clientY, ox: x.value, oy: floorY.value, id: e.pointerId, moved: false }
  el.setPointerCapture?.(e.pointerId)
}
function onPointerMove(e: PointerEvent) {
  if (!drag || e.pointerId !== drag.id) return
  const dx = e.clientX - drag.sx
  const dy = e.clientY - drag.sy
  if (!drag.moved) {
    if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return // 没过阈值 → 还算「点」,不动
    drag.moved = true
    dragging.value = true // 遛/小动作已在 pointerdown 冻住,这里只标记拖动态
  }
  const bx = petBounds()
  const by = floorBounds()
  x.value = Math.min(bx.max, Math.max(bx.min, drag.ox + dx))
  floorY.value = Math.min(by.max, Math.max(by.min, drag.oy - dy)) // 屏幕 y 向下、bottom 向上 → 减 dy
}
function onPointerUp(e: PointerEvent) {
  if (!drag || e.pointerId !== drag.id) return
  const wasDrag = drag.moved
  try {
    ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
  } catch {
    /* 已自动释放 */
  }
  drag = null
  if (wasDrag) {
    dragging.value = false
    savePos() // 记住落点
    if (petEnabled.value && !chatOpen.value) scheduleWalk(900) // 放下 → 接着遛
  } else {
    toggleChat() // 没拖动 = 普通点击 → 开/收聊天
  }
}
function onPointerCancel() {
  if (!drag) return
  const wasDrag = drag.moved
  drag = null
  dragging.value = false
  if (wasDrag && petEnabled.value && !chatOpen.value) scheduleWalk(900)
}

function onResize() {
  winW.value = window.innerWidth
  winH.value = window.innerHeight
  const { min, max } = petBounds()
  // 缩窗后蟹可能落到了屏外。必须用 0 过渡瞬时夹回:否则只改了目标 x,正在走的
  // 那条长 transition(慢蟹一段路最长十几秒)还卡在旧坐标(此刻已在新窗口右侧屏外)
  // 慢慢往回挪,视觉上「一缩小就消失」好几秒。
  if (x.value < min || x.value > max) {
    dur.value = 0
    x.value = Math.min(max, Math.max(min, x.value))
  }
  const fb = floorBounds()
  if (floorY.value < fb.min || floorY.value > fb.max) {
    floorY.value = Math.min(fb.max, Math.max(fb.min, floorY.value))
  }
}

watch(petEnabled, (on) => {
  if (on) {
    const { min, max } = petBounds()
    if (x.value < min || x.value > max) x.value = Math.round((min + max) / 2)
    scheduleWalk(800)
    void loadPetState()
    startInboxPoll()
  } else {
    clearTimeout(walkTimer)
    clearTimeout(stopTimer)
    clearAct()
    moving.value = false
    chatOpen.value = false
    remindersOpen.value = false
    controller?.abort()
    stopInboxPoll()
  }
})

// 账号切换 / 登出 → 清气泡里的历史,避免共享电脑串场(服务端按 user 隔离,这里 UI 兜底)。
watch(
  () => auth.token,
  () => {
    newChat()
    reminderQueue.length = 0
    reminders.value = []
    stopInboxPoll()
    void loadPetState()
    if (petEnabled.value && hasPetCap.value) startInboxPoll()
  }
)

onMounted(() => {
  window.addEventListener('resize', onResize)
  const saved = loadPos() // 恢复上次拖到的位置(夹回当前窗口范围)
  if (saved) {
    const bx = petBounds()
    const by = floorBounds()
    x.value = Math.min(bx.max, Math.max(bx.min, saved.x))
    floorY.value = Math.min(by.max, Math.max(by.min, saved.y))
  }
  if (petEnabled.value) {
    scheduleWalk(900)
    void loadPetState()
    startInboxPoll()
  }
})
onUnmounted(() => {
  window.removeEventListener('resize', onResize)
  stopInboxPoll()
  clearTimeout(walkTimer)
  clearTimeout(stopTimer)
  clearTimeout(actTimer)
  clearTimeout(moltAnimTimer)
  clearTimeout(moltToastTimer)
  clearTimeout(reminderToastTimer)
  controller?.abort()
})
</script>

<template>
  <div v-if="petEnabled && hasPetCap && !auth.isImpersonating" class="pet-root">
    <!-- 头顶对话气泡 -->
    <transition name="pet-bubble">
      <div
        v-if="chatOpen"
        class="pet-chat"
        :style="{ left: bubbleLeft + 'px', bottom: floorY + SIZE + 8 + 'px' }"
      >
        <header class="pc-head">
          <span class="pc-title">
            <!-- 顶栏小蟹:手绘 SVG(固定鲜红,不随心情染色),比 emoji 更精神。 -->
            <svg class="pc-crab" viewBox="0 0 24 24" aria-hidden="true">
              <g class="pc-crab-line">
                <path d="M7.5 15 L3.2 16.4" />
                <path d="M7.5 16.6 L3.4 19" />
                <path d="M16.5 15 L20.8 16.4" />
                <path d="M16.5 16.6 L20.6 19" />
                <path d="M8.4 12.6 L3.6 9.4" />
                <path d="M15.6 12.6 L20.4 9.4" />
              </g>
              <circle class="pc-crab-claw" cx="3" cy="8.6" r="2.1" />
              <circle class="pc-crab-claw" cx="21" cy="8.6" r="2.1" />
              <ellipse class="pc-crab-shell" cx="12" cy="14" rx="7" ry="5" />
              <circle class="pc-crab-eye" cx="9.7" cy="10.4" r="1.7" />
              <circle class="pc-crab-eye" cx="14.3" cy="10.4" r="1.7" />
              <circle class="pc-crab-pupil" cx="9.7" cy="10.6" r="0.8" />
              <circle class="pc-crab-pupil" cx="14.3" cy="10.6" r="0.8" />
            </svg>
            小蟹
          </span>
          <div class="pc-actions">
            <button
              type="button"
              class="pc-icon"
              :class="{ active: remindersOpen }"
              title="定时提醒"
              @click="toggleReminders"
            >
              <i class="ti ti-alarm" aria-hidden="true"></i>
            </button>
            <button type="button" class="pc-icon" title="开新对话" @click="newChat">
              <i class="ti ti-refresh" aria-hidden="true"></i>
            </button>
            <button type="button" class="pc-icon" title="收起" @click="toggleChat">
              <i class="ti ti-x" aria-hidden="true"></i>
            </button>
          </div>
        </header>

        <div v-if="remindersOpen" class="pc-reminders">
          <div class="pc-rem-top">
            <button type="button" class="pc-rem-back" title="返回聊天" @click="toggleReminders">
              <i class="ti ti-chevron-left" aria-hidden="true"></i> 返回
            </button>
          </div>
          <p class="pc-rem-hint">可以自己设定提醒任务哦</p>
          <div class="pc-rem-form">
            <input
              v-model="remTitle"
              class="pc-box"
              type="text"
              placeholder="提醒做什么,如:检查 A 客户出货"
            />
            <select v-model="remSchedule" class="pc-select">
              <option value="once">一次性</option>
              <option value="daily">每天</option>
              <option value="weekly">每周</option>
              <option value="every">每隔几小时</option>
            </select>
            <input
              v-if="remSchedule === 'once'"
              v-model="remOnceLocal"
              class="pc-box"
              type="datetime-local"
            />
            <input
              v-if="remSchedule === 'daily' || remSchedule === 'weekly'"
              v-model="remTime"
              class="pc-box"
              type="time"
            />
            <select v-if="remSchedule === 'weekly'" v-model.number="remWeekday" class="pc-select">
              <option :value="1">周一</option>
              <option :value="2">周二</option>
              <option :value="3">周三</option>
              <option :value="4">周四</option>
              <option :value="5">周五</option>
              <option :value="6">周六</option>
              <option :value="0">周日</option>
            </select>
            <input
              v-if="remSchedule === 'every'"
              v-model.number="remEveryHours"
              class="pc-box"
              type="number"
              min="1"
              max="168"
              placeholder="间隔(小时)"
            />
            <button
              type="button"
              class="pc-rem-add"
              :disabled="reminderSaving || !remTitle.trim()"
              @click="saveReminder()"
            >
              {{ reminderSaving ? '保存中…' : '添加提醒' }}
            </button>
          </div>
          <div v-if="remindersLoading" class="pc-rem-empty">加载中…</div>
          <ul v-else-if="reminders.length" class="pc-rem-list">
            <li v-for="r in reminders" :key="r.id" class="pc-rem-item" :class="{ off: !r.enabled }">
              <div class="pc-rem-meta">
                <span class="pc-rem-title">{{ r.title }}</span>
                <span class="pc-rem-sub">{{ formatReminderSchedule(r) }}</span>
              </div>
              <div class="pc-rem-btns">
                <span v-if="reminderDone(r)" class="pc-rem-done" title="已完成">已完成</span>
                <button v-else type="button" class="pc-icon sm" :title="r.enabled ? '暂停' : '启用'" @click="toggleReminderEnabled(r)">
                  <i :class="['ti', r.enabled ? 'ti-player-pause' : 'ti-player-play']" aria-hidden="true"></i>
                </button>
                <button type="button" class="pc-icon sm" title="删除" @click="removeReminder(r.id)">
                  <i class="ti ti-trash" aria-hidden="true"></i>
                </button>
              </div>
            </li>
          </ul>
        </div>

        <div v-else ref="scrollEl" class="pc-body">
          <p v-if="messages.length === 0" class="pc-intro">
            你好呀~ 我是小蟹 🦀 想聊点啥都行！<br />
            除了聊天,我也可以给你设定提醒任务哦！
          </p>
          <div
            v-for="(m, i) in messages"
            :key="i"
            class="pc-msg"
            :class="m.role === 'user' ? 'is-user' : 'is-ai'"
          >
            <div class="pc-bubble">
              <span v-if="m.content">{{ m.content }}</span>
              <span v-else class="pc-typing" aria-label="正在输入"><i></i><i></i><i></i></span>
            </div>
          </div>
        </div>

        <footer v-if="!remindersOpen" class="pc-input">
          <input
            v-model="input"
            class="pc-box"
            type="text"
            placeholder="问小蟹…"
            @keydown.enter.prevent="send()"
          />
          <button
            type="button"
            class="pc-send"
            :disabled="sending || !input.trim()"
            :title="sending ? '回复中…' : '发送'"
            @click="send()"
          >
            <i :class="['ti', sending ? 'ti-loader-2 spin' : 'ti-send']" aria-hidden="true"></i>
          </button>
        </footer>

        <span class="pc-tail" :style="{ left: tailLeft + 'px' }"></span>
      </div>
    </transition>

    <!-- 蜕壳 / 到点提醒 文案小气泡(短暂浮现,不接收点击) -->
    <transition name="pet-molt">
      <div
        v-if="moltToast || reminderToast"
        class="pet-molt-toast"
        :class="{ 'is-reminder': !!reminderToast }"
        :style="{ left: x + SIZE / 2 + 'px', bottom: floorY + SIZE * petScale + 12 + 'px' }"
      >
        {{ reminderToast ?? moltToast }}
      </div>
    </transition>

    <!-- 螃蟹本体(手绘 SVG:腿会爬、眼会眨、钳子会挥;比 emoji 多了「活气」) -->
    <button
      ref="crabEl"
      type="button"
      class="pet-crab"
      :class="[
        `mood-${moodTier}`,
        {
          moving,
          chatting: chatOpen,
          dragging,
          molting,
          'act-wave': act === 'wave',
          'act-cheer': act === 'cheer',
          'act-hop': act === 'hop',
          'act-pinch': act === 'pinch'
        }
      ]"
      :style="{ left: x + 'px', bottom: floorY + 'px', transitionDuration: dur + 's' }"
      :title="chatOpen ? '收起对话' : '点我聊聊 · 按住可拖动'"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerCancel"
      @keydown.enter.prevent="toggleChat"
      @keydown.space.prevent="toggleChat"
    >
      <span class="crab-wrap" :style="{ transform: `scaleX(${facing}) scale(${petScale})` }">
        <svg class="crab" viewBox="-22 -8 144 102" aria-hidden="true">
          <!-- 六条腿(三足交替):走动时划动,站定时轻微呼吸 -->
          <g class="legs">
            <path class="leg l1" d="M30 52 Q18 54 12 62" />
            <path class="leg l2" d="M28 56 Q15 60 9 70" />
            <path class="leg l3" d="M30 60 Q18 66 14 76" />
            <path class="leg r1" d="M70 52 Q82 54 88 62" />
            <path class="leg r2" d="M72 56 Q85 60 91 70" />
            <path class="leg r3" d="M70 60 Q82 66 86 76" />
          </g>
          <!-- 两只钳子(细钳 + 适中臂长):钳掌+下指(固定) + 上指(dactyl,可动,act-pinch 时夹合);钳口朝内上开,act-wave 时整只举起挥 -->
          <g class="claw claw-l">
            <path class="arm" d="M28 49 Q11 39 -2 29" />
            <path class="claw-fixed" d="M-3 28 C-8 29 -9 37 -4 39 C3 41 9 38 11 32 C7 32 2 30 -2 30 Z" />
            <ellipse class="claw-shine" cx="-4" cy="34" rx="3" ry="2.4" />
            <path class="dactyl dactyl-l" d="M-2 28 C3 25 7 21 9 18 C7 22 3 26 -1 29 Z" />
          </g>
          <g class="claw claw-r">
            <path class="arm" d="M72 49 Q89 39 102 29" />
            <path class="claw-fixed" d="M103 28 C108 29 109 37 104 39 C97 41 91 38 89 32 C93 32 98 30 102 30 Z" />
            <ellipse class="claw-shine" cx="104" cy="34" rx="3" ry="2.4" />
            <path class="dactyl dactyl-r" d="M102 28 C97 25 93 21 91 18 C93 22 97 26 101 29 Z" />
          </g>
          <!-- 壳 + 肚皮高光 + 嘴 -->
          <ellipse class="shell" cx="50" cy="50" rx="27" ry="17.5" />
          <ellipse class="belly" cx="50" cy="55" rx="17" ry="8" />
          <ellipse class="shine" cx="40" cy="43" rx="9" ry="5" />
          <path class="mouth" d="M43 57 Q50 62 57 57" />
          <!-- 眼柄 + 会眨会扫视的眼睛 -->
          <path class="stalk" d="M44 37 L41 23" />
          <path class="stalk" d="M56 37 L59 23" />
          <g class="eye eye-l">
            <circle class="eye-white" cx="41" cy="20" r="7" />
            <circle class="pupil" cx="41" cy="20" r="3.2" />
            <circle class="pupil-shine" cx="42.4" cy="18.6" r="1.2" />
          </g>
          <g class="eye eye-r">
            <circle class="eye-white" cx="59" cy="20" r="7" />
            <circle class="pupil" cx="59" cy="20" r="3.2" />
            <circle class="pupil-shine" cx="60.4" cy="18.6" r="1.2" />
          </g>
        </svg>
      </span>
    </button>
  </div>
</template>

<style scoped>
/* 满屏定位层,但不挡点击:只有螃蟹和气泡接收事件,其余穿透到 App。 */
.pet-root {
  position: fixed;
  inset: 0;
  /* 必须低于弹窗层(modal-mask z-index:100):否则可拖拽的螃蟹(.pet-crab pointer-events:auto)
     游走到弹窗按钮/输入框上会拦截点击,导致"登记出货/确认收款"等点不动。
     80 > AppLayout(50),非弹窗时螃蟹仍浮于页面内容之上。 */
  z-index: 80;
  pointer-events: none;
}

/* ── 螃蟹 ── */
.pet-crab {
  position: absolute;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: grab;
  pointer-events: auto;
  user-select: none;
  touch-action: none;
  display: flex;
  align-items: center;
  justify-content: center;
  /* 只过渡 left(横移);时长走内联,冻结时设 0 */
  transition-property: left;
  transition-timing-function: linear;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.22));
}
.pet-crab:hover {
  filter: drop-shadow(0 3px 5px rgba(0, 0, 0, 0.28));
}
/* 拖动中:抓手光标 + 抬起来的阴影 */
.pet-crab.dragging {
  cursor: grabbing;
  filter: drop-shadow(0 6px 9px rgba(0, 0, 0, 0.32));
}
/* 分层:朝向翻转(scaleX)在 wrap 上、身体上下颠(translateY)在按钮上、
   腿/眼/钳子各自在 SVG 内部动 —— 互不覆盖。 */
.crab-wrap {
  display: inline-flex;
  transition: transform 0.15s ease;
}
.crab {
  display: block;
  width: 48px;
  height: auto;
  overflow: visible;
  --shell: #f0603f;
  --shell-d: #c63016;
  --belly: #ff9d80;
  --leg: #c63016;
  --eye: #fff;
  --pupil: #2b211c;
}
/* 心情染色:覆盖螃蟹自己的局部色变量(不碰 brand/框架色)。好=基准暖橙;
   一般=略降饱和;不好=往青冷偏移。切档时颜色平滑过渡(见下方 transition)。 */
.pet-crab.mood-normal .crab {
  --shell: #e0775f;
  --shell-d: #b5482f;
  --belly: #f0a98f;
  --leg: #b5482f;
}
.pet-crab.mood-bad .crab {
  --shell: #6f9f97;
  --shell-d: #3f6f66;
  --belly: #a6ccc5;
  --leg: #3f6f66;
}
/* 颜色随心情渐变几秒,不硬切(fill/stroke 的值来自上面的变量,变量变→属性过渡)。 */
.crab .shell,
.crab .belly,
.crab .leg,
.crab .arm,
.crab .claw-fixed,
.crab .dactyl,
.crab .mouth,
.crab .stalk,
.crab .eye-white {
  transition: fill 0.8s ease, stroke 0.8s ease;
}
.crab .shell {
  fill: var(--shell);
  stroke: var(--shell-d);
  stroke-width: 2.2;
}
.crab .belly {
  fill: var(--belly);
  opacity: 0.5;
}
.crab .shine {
  fill: #fff;
  opacity: 0.22;
}
.crab .mouth {
  fill: none;
  stroke: var(--shell-d);
  stroke-width: 2;
  stroke-linecap: round;
}
.crab .stalk {
  fill: none;
  stroke: var(--shell-d);
  stroke-width: 3;
  stroke-linecap: round;
}
.crab .eye-white {
  fill: var(--eye);
  stroke: var(--shell-d);
  stroke-width: 1.6;
}
.crab .pupil {
  fill: var(--pupil);
}
.crab .pupil-shine {
  fill: #fff;
}
.crab .leg {
  fill: none;
  stroke: var(--leg);
  stroke-width: 3.4;
  stroke-linecap: round;
  transform-box: view-box;
}
.crab .arm {
  fill: none;
  stroke: var(--shell-d);
  stroke-width: 4;
  stroke-linecap: round;
}
.crab .claw-fixed,
.crab .dactyl {
  fill: var(--shell);
  stroke: var(--shell-d);
  stroke-width: 2.2;
  stroke-linejoin: round;
}
.crab .dactyl {
  transform-box: view-box;
}
.crab .dactyl-r {
  transform-origin: 102px 29px;
}
.crab .dactyl-l {
  transform-origin: -2px 29px;
}
.crab .claw-shine {
  fill: #fff;
  opacity: 0.2;
}

/* 各部件的旋转支点(viewBox 坐标系) */
.crab .claw {
  transform-box: view-box;
}
.crab .claw-l {
  transform-origin: 28px 48px;
}
.crab .claw-r {
  transform-origin: 72px 48px;
}
.crab .eye {
  transform-box: view-box;
}
.crab .eye-l {
  transform-origin: 41px 20px;
}
.crab .eye-r {
  transform-origin: 59px 20px;
}
.crab .pupil {
  transform-box: view-box;
}
.crab .l1 {
  transform-origin: 30px 52px;
}
.crab .l2 {
  transform-origin: 28px 56px;
}
.crab .l3 {
  transform-origin: 30px 60px;
}
.crab .r1 {
  transform-origin: 70px 52px;
}
.crab .r2 {
  transform-origin: 72px 56px;
}
.crab .r3 {
  transform-origin: 70px 60px;
}

/* 常驻小动作:腿轻微呼吸、眨眼、瞳孔扫视 —— 站着也像活的。 */
.crab .leg {
  animation: crab-leg-idle 2.6s ease-in-out infinite;
}
.crab .l1,
.crab .r2,
.crab .l3 {
  animation-delay: 0s;
}
.crab .r1,
.crab .l2,
.crab .r3 {
  animation-delay: -1.3s;
}
.crab .eye {
  animation: crab-blink 4.2s ease-in-out infinite;
}
.crab .eye-r {
  animation-delay: 0.18s;
}
.crab .pupil {
  animation: crab-glance 5.4s ease-in-out infinite;
}

/* 走动:腿加速划动(三足交替),身体上下颠。 */
.pet-crab.moving .crab .leg {
  animation: crab-leg-walk 0.34s ease-in-out infinite;
}
.pet-crab.moving .crab .r1,
.pet-crab.moving .crab .l2,
.pet-crab.moving .crab .r3 {
  animation-delay: -0.17s;
}
.pet-crab.moving {
  animation: crab-bob 0.34s infinite ease-in-out;
}

/* 一次性:挥钳 / 蹦跳 / 夹钳(JS 加 act-* 类触发) */
.pet-crab.act-wave .crab .claw-r {
  animation: crab-wave 0.9s ease-in-out;
}
.pet-crab.act-cheer .crab .claw-r {
  animation: crab-wave 0.9s ease-in-out;
}
.pet-crab.act-cheer .crab .claw-l {
  animation: crab-wave-l 0.9s ease-in-out;
}
.pet-crab.act-hop {
  animation: crab-hop 0.62s ease;
}
.pet-crab.act-pinch .crab .dactyl-r {
  animation: crab-pinch-r 0.9s ease-in-out;
}
.pet-crab.act-pinch .crab .dactyl-l {
  animation: crab-pinch-l 0.9s ease-in-out;
}

@keyframes crab-leg-idle {
  0%,
  100% {
    transform: rotate(2.5deg);
  }
  50% {
    transform: rotate(-2.5deg);
  }
}
@keyframes crab-leg-walk {
  0%,
  100% {
    transform: rotate(9deg);
  }
  50% {
    transform: rotate(-9deg);
  }
}
@keyframes crab-blink {
  0%,
  90%,
  100% {
    transform: scaleY(1);
  }
  95% {
    transform: scaleY(0.12);
  }
}
@keyframes crab-glance {
  0%,
  28% {
    transform: translateX(0);
  }
  42%,
  66% {
    transform: translateX(1.4px);
  }
  80%,
  100% {
    transform: translateX(-1.2px);
  }
}
@keyframes crab-bob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}
@keyframes crab-hop {
  0%,
  100% {
    transform: translateY(0);
  }
  45% {
    transform: translateY(-10px);
  }
}
@keyframes crab-wave {
  0%,
  100% {
    transform: rotate(0);
  }
  20% {
    transform: rotate(-30deg);
  }
  40% {
    transform: rotate(-12deg);
  }
  60% {
    transform: rotate(-30deg);
  }
  80% {
    transform: rotate(-12deg);
  }
}
/* 双钳齐挥:左钳镜像,用正角度,跟右钳同节奏一起举起来挥。 */
@keyframes crab-wave-l {
  0%,
  100% {
    transform: rotate(0);
  }
  20% {
    transform: rotate(30deg);
  }
  40% {
    transform: rotate(12deg);
  }
  60% {
    transform: rotate(30deg);
  }
  80% {
    transform: rotate(12deg);
  }
}
/* 夹钳:上指连夹两下(15%/45% 合,中间略松),再复位张开。左钳镜像反向。 */
@keyframes crab-pinch-r {
  0%,
  60%,
  100% {
    transform: rotate(0);
  }
  15%,
  45% {
    transform: rotate(20deg);
  }
  30% {
    transform: rotate(3deg);
  }
}
@keyframes crab-pinch-l {
  0%,
  60%,
  100% {
    transform: rotate(0);
  }
  15%,
  45% {
    transform: rotate(-20deg);
  }
  30% {
    transform: rotate(-3deg);
  }
}

/* ── 蜕壳成长「时刻」 ── */
/* 变大那一下放慢成带回弹的成长(scale 已由 petScale 抬高),配壳面一闪像换了层新壳。 */
.pet-crab.molting .crab-wrap {
  transition: transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.pet-crab.molting .crab {
  animation: crab-molt 1s ease;
}
@keyframes crab-molt {
  0% {
    filter: brightness(1);
  }
  25% {
    filter: brightness(1.5);
  }
  100% {
    filter: brightness(1);
  }
}
/* 蜕壳文案小气泡:短暂浮现在头顶,不挡点击。 */
.pet-molt-toast {
  position: absolute;
  transform: translateX(-50%);
  pointer-events: none;
  padding: 5px 10px;
  background: var(--color-background-primary);
  border: 0.5px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-md);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.16);
  font-size: var(--fs-control);
  color: var(--color-text-primary);
  white-space: nowrap;
  z-index: 1;
}
.pet-molt-enter-active,
.pet-molt-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.pet-molt-enter-from,
.pet-molt-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(6px);
}

/* ── 头顶气泡聊天框 ── */
.pet-chat {
  position: absolute;
  /* bottom 由内联 style 给(= FLOOR + 螃蟹 + 间隙),跟 FLOOR 联动,改 FLOOR 不会错位 */
  width: 300px;
  max-width: calc(100vw - 16px);
  /* 高度自适应:短对话小巧(min-height 保底,≈ 原 340 + 两行),内容多了自动往上长,
     长到 max-height 封顶后正文内部滚动(见 .pc-body overflow)。min-height 随视口收窄,
     避免矮窗时气泡顶到屏外。 */
  height: auto;
  min-height: min(384px, 60vh);
  max-height: min(560px, 72vh, calc(100vh - 96px));
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  background: var(--color-background-primary);
  border: 0.5px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 10px 36px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}
/* 指向螃蟹的小尾巴 */
.pc-tail {
  position: absolute;
  bottom: -7px;
  width: 14px;
  height: 14px;
  background: var(--color-background-primary);
  border-right: 0.5px solid var(--color-border-tertiary);
  border-bottom: 0.5px solid var(--color-border-tertiary);
  transform: translateX(-50%) rotate(45deg);
}

.pc-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  background: var(--color-background-secondary);
  border-bottom: 0.5px solid var(--color-border-tertiary);
}
.pc-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-item);
  font-weight: var(--fw-med);
  color: var(--color-text-primary);
}
/* 顶栏小蟹图标:鲜红壳 + 深红描边(固定色,不随心情变淡)。 */
.pc-crab {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  overflow: visible;
}
.pc-crab-shell {
  fill: #f0603f;
  stroke: #c63016;
  stroke-width: 1.3;
}
.pc-crab-claw {
  fill: #f0603f;
  stroke: #c63016;
  stroke-width: 1.3;
}
.pc-crab-line path {
  fill: none;
  stroke: #c63016;
  stroke-width: 1.7;
  stroke-linecap: round;
}
.pc-crab-eye {
  fill: #fff;
  stroke: #c63016;
  stroke-width: 0.9;
}
.pc-crab-pupil {
  fill: #2b211c;
}
.pc-actions {
  display: flex;
  gap: 2px;
}
.pc-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: var(--border-radius-md);
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
  font-family: inherit;
}
.pc-icon:hover {
  background: var(--color-background-primary);
  color: var(--color-text-primary);
}
.pc-icon i {
  font-size: 16px;
}

.pc-body {
  /* flex-basis:auto(非 0)→ 正文把自身内容高度算进气泡自适应高度里,气泡才会随对话变高;
     grow 撑满短对话的留白(输入栏贴底)、shrink+min-height:0 让长对话在封顶后正文内部滚动。 */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pc-intro {
  margin: auto 0;
  font-size: var(--fs-control);
  color: var(--color-text-secondary);
  line-height: 1.6;
  text-align: center;
}
.pc-msg {
  display: flex;
  max-width: 100%;
}
.pc-msg.is-user {
  justify-content: flex-end;
}
.pc-msg.is-ai {
  justify-content: flex-start;
}
.pc-bubble {
  max-width: 84%;
  padding: 7px 10px;
  border-radius: 11px;
  font-size: var(--fs-control);
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.pc-msg.is-user .pc-bubble {
  background: var(--color-brand);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.pc-msg.is-ai .pc-bubble {
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
  border-bottom-left-radius: 4px;
}

.pc-typing {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  padding: 2px 0;
}
.pc-typing i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--color-text-tertiary);
  animation: pc-blink 1.2s infinite ease-in-out both;
}
.pc-typing i:nth-child(2) {
  animation-delay: 0.2s;
}
.pc-typing i:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes pc-blink {
  0%,
  80%,
  100% {
    opacity: 0.25;
  }
  40% {
    opacity: 1;
  }
}

.pc-icon.active {
  color: var(--color-brand);
}
.pc-icon.sm {
  width: 28px;
  height: 28px;
  font-size: 15px;
}

.pc-reminders {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pc-rem-top {
  display: flex;
}
.pc-rem-back {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 0;
  background: none;
  border: none;
  font: inherit;
  font-size: var(--fs-control);
  color: var(--color-text-secondary);
  cursor: pointer;
}
.pc-rem-back:hover {
  color: var(--color-text-primary);
}
.pc-rem-back i {
  font-size: 15px;
}
.pc-rem-hint {
  margin: 0;
  font-size: var(--fs-caption);
  color: var(--color-text-tertiary);
  line-height: 1.45;
  text-align: center;
}
.pc-rem-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pc-select {
  padding: 8px 10px;
  border: 0.5px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-md);
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
  font: inherit;
  font-size: var(--fs-control);
}
.pc-rem-add {
  padding: 8px 12px;
  border: none;
  border-radius: var(--border-radius-md);
  background: var(--color-brand);
  color: #fff;
  font: inherit;
  font-size: var(--fs-control);
  cursor: pointer;
}
.pc-rem-add:disabled {
  opacity: 0.7;
  cursor: default;
}
.pc-rem-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pc-rem-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-radius: var(--border-radius-md);
  background: var(--color-background-secondary);
  border: 0.5px solid var(--color-border-tertiary);
}
.pc-rem-item.off {
  opacity: 0.55;
}
.pc-rem-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.pc-rem-title {
  font-size: var(--fs-control);
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pc-rem-sub {
  font-size: var(--fs-caption);
  color: var(--color-text-tertiary);
}
.pc-rem-btns {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.pc-rem-done {
  font-size: var(--fs-caption);
  color: var(--color-text-tertiary);
  padding: 0 4px;
}
.pc-rem-empty {
  margin: 0;
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: var(--fs-caption);
  padding: 12px 0;
}

.pet-molt-toast.is-reminder {
  max-width: min(320px, calc(100vw - 24px));
  white-space: normal;
  text-align: center;
  line-height: 1.4;
}

.pc-input {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 10px;
  border-top: 0.5px solid var(--color-border-tertiary);
}
.pc-box {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border: 0.5px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-md);
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
  font: inherit;
  font-size: var(--fs-control);
  outline: none;
}
.pc-box:focus {
  border-color: var(--color-brand);
}
.pc-send {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: var(--border-radius-md);
  background: var(--color-brand);
  color: #fff;
  cursor: pointer;
  font-family: inherit;
}
.pc-send:hover:not(:disabled) {
  filter: brightness(1.06);
}
.pc-send:disabled {
  opacity: 0.85;
  cursor: default;
}
.pc-send i {
  font-size: 16px;
}
.spin {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── 气泡出现:从底部轻轻冒出 ── */
.pet-bubble-enter-active,
.pet-bubble-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
  transform-origin: bottom center;
}
.pet-bubble-enter-from,
.pet-bubble-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}
</style>
